// src/pages/QuestionsPage.tsx
// ✅ CORRIGIDO:
//   - Busca todos os IDs filtrados e passa allQuestionIds para QuestionContainer
//   - Navegação respeita o filtro ativo

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { QuestionFilters as Filters } from '../types/question.types';
import { questionService } from '../services/questionService';
import QuestionFilters from '../components/question/QuestionFilters';
import QuestionContainer from '../components/question/QuestionContainer';
import './QuestionsPage.css';
import { BookOpen } from 'lucide-react';

interface QuestionsPageProps {
    onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
    onSwitchToManage?: (questionId: string) => void;
}

// ─── Log local de respostas para filtro de tempo ──────────────
export const logAnswer = (isCorrect: boolean) => {
    try {
        const stored = localStorage.getItem('graia_answers_log');
        const log: { ts: number; correct: boolean }[] = stored ? JSON.parse(stored) : [];
        log.push({ ts: Date.now(), correct: isCorrect });
        const cutoff24h = Date.now() - 86_400_000;
        const trimmed = log.filter(e => e.ts >= cutoff24h);
        localStorage.setItem('graia_answers_log', JSON.stringify(trimmed));
    } catch { /* silencioso */ }
};

// ─────────────────────────────────────────────────────────────

const QuestionsPage: React.FC<QuestionsPageProps> = ({
    onShowToast,
    onSwitchToManage,
}) => {
    const { isAdmin } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(
        searchParams.get('q')
    );
    const [filters, setFilters] = useState<Filters>({});
    const [filteredIds, setFilteredIds] = useState<string[]>([]);
    const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);
    const [freeQuestionIds, setFreeQuestionIds] = useState<string[]>([]);
    const freeQuestionIdsRef = useRef<string[]>([]);
    const [hasFullAccess, setHasFullAccess] = useState(false);

    // Verifica se o usuário tem "Acesso Total" (compra do tipo access_all)
    useEffect(() => {
        if (isAdmin) { setHasFullAccess(true); return; }
        const token = localStorage.getItem('accessToken');
        fetch('/api/users/my-purchases', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (!data?.purchases) return;
                const active = data.purchases.some(
                    (p: any) => p.productId?.type === 'access_all'
                );
                setHasFullAccess(active);
            })
            .catch(() => {});
    }, [isAdmin]);

    // Carrega somente os IDs para montar a fila de navegação (endpoint leve, sem documentos completos)
    const loadFilteredIds = useCallback(async (activeFilters: Filters, isInitial = false) => {
        try {
            if (isInitial) setInitialLoading(true);
            else setFilterLoading(true);

            const data = await questionService.getQuestionIds(activeFilters);

            const ids: string[] = data.ids;

            // ✅ Embaralhar apenas os não-gratuitos
            const freeIds = freeQuestionIdsRef.current;
            const freeOnes = ids.filter(id => freeIds.includes(id));
            const nonFree = ids.filter(id => !freeIds.includes(id));

            // Fisher-Yates só nos não-gratuitos
            for (let i = nonFree.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [nonFree[i], nonFree[j]] = [nonFree[j], nonFree[i]];
            }

            // Gratuitas primeiro, depois o resto embaralhado
            const ordered = [...freeOnes, ...nonFree];

            setFilteredIds(ordered);
            setTotalCount(ordered.length);

            const firstId = ordered[0] || null;
            setCurrentQuestionId(prev => {
                if (ordered.length === 0) return null;
                if (prev && ordered.includes(prev)) return prev;
                return firstId;
            });

            if (ordered.length > 0) {
                setSearchParams(prev => {
                    const current = prev.get('q');
                    if (current && ordered.includes(current)) return prev;
                    return { q: ordered[0] };
                });
            } else {
                setSearchParams({});
            }
        } catch {
            onShowToast?.('Erro ao carregar questões', 'error');
        } finally {
            setInitialLoading(false);
            setFilterLoading(false);
        }
    }, [setSearchParams, onShowToast]);

    // Carrega IDs quando os filtros mudam
    useEffect(() => {
        const isInitial = filteredIds.length === 0;
        loadFilteredIds(filters, isInitial);
    }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        fetch('/api/admin/free-config/question-config', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                console.log('📡 question-config status:', res.status);
                return res.ok ? res.json() : null;
            })
            .then(data => {
                if (data?.freeQuestionIds) {
                    // ✅ Se disabled, trata como lista vazia (tudo bloqueado)
                    const ids = data.enabled ? data.freeQuestionIds : [];
                    setFreeQuestionIds(ids);
                    freeQuestionIdsRef.current = ids;
                }
            })
            .catch(err => console.error('❌ question-config error:', err));
    }, []);

    const handleFiltersChange = (newFilters: Filters) => {
        setFilters(newFilters);
        // loadFilteredIds será chamado pelo useEffect acima
    };

    const handleQuestionChange = (questionId: string) => {
        setCurrentQuestionId(questionId);
        setSearchParams({ q: questionId });
    };

    return (
        <div className="qstpag-page">
            <div className="qstpag-content">
                {initialLoading ? (
                    <div className="qstpag-loading">
                        <div className="qstpag-spinner" />
                        <p>Carregando questões...</p>
                    </div>
                ) : currentQuestionId ? (
                    <QuestionContainer
                        questionId={currentQuestionId}
                        onQuestionChange={handleQuestionChange}
                        onShowToast={onShowToast}
                        allQuestionIds={filteredIds}
                        freeQuestionIds={freeQuestionIds}
                        hasFullAccess={hasFullAccess}
                        onEditQuestion={isAdmin ? (id) => { onSwitchToManage?.(id); } : undefined}
                        filtersBar={
                            <div className="qstpag-filters-bar">
                                <QuestionFilters
                                    onFiltersChange={handleFiltersChange}
                                    initialFilters={filters}
                                    totalQuestions={totalCount}
                                    loading={filterLoading}
                                />
                            </div>
                        }
                    />
                ) : (
                    <>
                        <div className="qstpag-filters-bar">
                            <QuestionFilters
                                onFiltersChange={handleFiltersChange}
                                initialFilters={filters}
                                totalQuestions={totalCount}
                                loading={filterLoading}
                            />
                        </div>
                        <div className="qstpag-empty">
                            <BookOpen size={44} />
                            <h3>Nenhuma questão encontrada</h3>
                            <p>Ajuste ou limpe os filtros para ver as questões</p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default QuestionsPage;